<script lang="ts" setup>
import { computed } from "vue";
import type { Product, ProductVersionSummary } from "@/types";
import { useProjectWorkflows } from "@/queries/projects";
import { useProductComponents, usePublisherProductMetrics } from "@/queries/products";
import { useConnectionTemplates } from "@/queries/connection-templates";
import { useSessionStore } from "@/stores/session-store";

interface Props {
  product: Product | null;
  productStatus: any;
  currentVersion: ProductVersionSummary | null;
}

const props = defineProps<Props>();
const sessionStore = useSessionStore();

const masterProjectId = computed(() => props.product?.masterProjectId ?? "");
const workflowsEnabled = computed(() => !!masterProjectId.value);
const { projectWorkflows } = useProjectWorkflows(masterProjectId, workflowsEnabled);

// Required connections are the connection slots declared across the product's workflows. The
// backend returns them as live-preview for publishers (see GetProductComponents) so the publisher
// sees slots they've just added without needing to publish a new product version first.
const productIdRef = computed(() => props.product?.id ?? "");
const componentsEnabled = computed(() => !!productIdRef.value);
const { productComponents, productComponentsLoading } = useProductComponents(productIdRef, componentsEnabled);
const requiredConnections = computed(() => productComponents.value?.connections ?? []);
const requiredDatastores = computed(() => productComponents.value?.datastores ?? []);

// connectionTemplateId is an opaque id; map it to the template's display name for the type label.
const { templates } = useConnectionTemplates();
const templateById = computed(() => new Map(templates.value.map((t) => [t.id, t])));
function templateLabel(templateId: string): string {
  return templateById.value.get(templateId)?.name ?? templateId;
}

// Real metrics from /api/orgs/{orgId}/publisher-products: subscriberCount, successRate, totalRuns.
// avgDuration and monthlyRevenue are still placeholders (no run-duration tracking; no billing
// model) — the UI greys those out.
const orgId = computed(() => sessionStore.session?.currentOrgId ?? "");
const { metricsByProduct } = usePublisherProductMetrics(orgId);
const productMetrics = computed(() => metricsByProduct.value.get(props.product?.id ?? ""));
const subscriberCount = computed(() => productMetrics.value?.subscriberCount ?? 0);
const totalRuns = computed(() => productMetrics.value?.totalRuns ?? 0);
const successRate = computed(() => productMetrics.value?.successRate ?? 0);

const versionWorkflows = computed(() => {
  if (!props.currentVersion) return [];
  return Object.entries(props.currentVersion.workflowVersionSnapshots).map(
    ([workflowId, snapshot]) => {
      const wf = projectWorkflows.value.find((w) => w.id === workflowId);
      return {
        id: workflowId,
        name: wf?.name ?? workflowId,
        description: wf?.description ?? "",
        version: snapshot.versionLabel,
      };
    },
  );
});

// Placeholder metric — no billing model exists. Greyed-out in the template.
const placeholderMetrics = {
  monthlyRevenue: 2847,
};
</script>

<template>
  <div class="overview-tab">
    <!-- Performance Metrics: pinned to the top so they're the first thing seen on the page. -->
    <div class="row g-3 mb-4">
      <!-- Subscribers: real data from /api/orgs/{orgId}/publisher-products. -->
      <div class="col-6 col-md-3">
        <div class="card text-center">
          <div class="card-body">
            <h3 class="text-primary mb-2">
              {{ subscriberCount.toLocaleString() }}
            </h3>
            <p class="text-muted small mb-2">Subscribers</p>
          </div>
        </div>
      </div>

      <!-- Success Rate: real, derived from RunHistory outcomes across master's workflows.
           Empty state when totalRuns is 0 so a brand-new product doesn't read a misleading "0%". -->
      <div class="col-6 col-md-3">
        <div class="card text-center">
          <div class="card-body">
            <h3 v-if="totalRuns > 0" class="text-success mb-2">{{ successRate.toFixed(1) }}%</h3>
            <h3 v-else class="text-muted mb-2 small mt-2">No runs yet</h3>
            <p class="text-muted small mb-2">Success Rate</p>
          </div>
        </div>
      </div>

      <!-- Total Runs: real, count of all completed runs across the product's master workflows. -->
      <div class="col-6 col-md-3">
        <div class="card text-center">
          <div class="card-body">
            <h3 class="text-warning mb-2">{{ totalRuns.toLocaleString() }}</h3>
            <p class="text-muted small mb-2">Total Runs</p>
          </div>
        </div>
      </div>

      <div class="col-6 col-md-3">
        <div
          class="card text-center metric-placeholder"
          title="Revenue tracking coming soon — placeholder data"
        >
          <div class="card-body">
            <h3 class="text-info mb-2">
              ${{ placeholderMetrics.monthlyRevenue.toLocaleString() }}
            </h3>
            <p class="text-muted small mb-2">Monthly Revenue</p>
            <small class="text-muted">Coming soon</small>
          </div>
        </div>
      </div>
    </div>

    <!-- Product Information -->
    <div class="card mb-4">
      <div class="card-body">
        <h2 class="card-title mb-3">Product Information</h2>
        <div class="mb-3">
          <strong>Description:</strong>
          <p class="text-muted mt-1 mb-0">
            {{ product?.description || "No description provided." }}
          </p>
        </div>

        <div class="mb-3">
          <strong>Included Workflows:</strong>
          <div v-if="!currentVersion" class="text-muted small mt-2">
            No published version yet — workflows will appear here once a version is published.
          </div>
          <div v-else-if="versionWorkflows.length === 0" class="text-muted small mt-2">
            This version contains no workflows.
          </div>
          <ul v-else class="list-group list-group-flush mt-2">
            <li
              v-for="workflow in versionWorkflows"
              :key="workflow.id"
              class="list-group-item d-flex justify-content-between align-items-center px-0 py-2"
            >
              <div class="me-2">
                <span class="fw-medium">{{ workflow.name }}</span>
                <span v-if="workflow.description" class="text-muted small ms-2">{{ workflow.description }}</span>
              </div>
              <span class="badge rounded-pill bg-primary">{{ workflow.version }}</span>
            </li>
          </ul>
        </div>

        <div class="mb-3">
          <strong>Required Connections:</strong>
          <div v-if="productComponentsLoading && requiredConnections.length === 0" class="text-muted small mt-2">
            Loading…
          </div>
          <div v-else-if="requiredConnections.length === 0" class="text-muted small mt-2">
            No connection slots declared yet — add a
            <code v-pre>{{ connection.&lt;slot&gt;.&lt;field&gt; }}</code>
            reference in a service config and the slot will appear here.
          </div>
          <div v-else class="mt-2 d-flex gap-2 flex-wrap">
            <span
              v-for="slot in requiredConnections"
              :key="slot.slotName"
              class="badge bg-primary"
              :title="`Type: ${templateLabel(slot.connectionTemplateId)}`"
            >
              {{ slot.slotName }}
            </span>
          </div>
        </div>

        <div>
          <strong>Required Datastores:</strong>
          <div v-if="productComponentsLoading && requiredDatastores.length === 0" class="text-muted small mt-2">
            Loading…
          </div>
          <div v-else-if="requiredDatastores.length === 0" class="text-muted small mt-2">
            No datastores declared in this product's workflows.
          </div>
          <div v-else class="mt-2 d-flex gap-2 flex-wrap">
            <span
              v-for="ds in requiredDatastores"
              :key="ds.name"
              class="badge bg-primary"
              :title="ds.description || undefined"
            >
              {{ ds.name }}
            </span>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<style scoped lang="scss">
/* COMMENTED OUT: Redundant - Bootstrap already handles responsive grid behavior */
/* Force column widths to ensure proper layout - 2 per row on mobile */
/*@media (max-width: 767.98px) {*/
/*  .row .col-6 {*/
/*    flex: 0 0 50%;*/
/*    max-width: 50%;*/
/*  }*/
/*}*/

/*@media (min-width: 768px) {*/
/*  .row .col-md-3 {*/
/*    flex: 0 0 25%;*/
/*    max-width: 25%;*/
/*  }*/
/*}*/

</style>
